import { Injectable, OnDestroy } from '@angular/core';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { TcsAudioPlayer } from 'src/app/shared/audio-player/tcs-audio-player';
import { TickService } from 'src/app/shared/tick.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/store/appstate';
import { observeCommunicationCenterFromRouteNavigationEnd } from 'src/models/communication-center-view-model';
import { RouterService } from 'src/app/shared/shared-base/amg-ui/router.service';
import { undefinedOrSwitchMap } from 'src/util/rxjs';
import { selectUnitSummaryEntitiesByCommunicationCenterId } from 'src/store/generated/unit-summary-record.generated';
import { shareReplaySafe } from 'src/util/rxjs/operators/share-replay-safe';
import { PositionReportAlertService } from './position-report-alert.service';

const soundPath = './assets/media/alarm1.mp3';

@Injectable({
  providedIn: 'root',
})
export class PositionReportAlertAudibleService implements OnDestroy {
  private readonly _player: TcsAudioPlayer;
  private readonly _playLoop$ = new Subject<boolean>();
  private _onDestroy$ = new Subject();

  constructor(
    private _tick: TickService,
    private _positionReport: PositionReportAlertService,
    private _router: RouterService,
    private _store: Store<AppState>,
  ) {
    this._player = new TcsAudioPlayer(soundPath, {
      load: true,
      volume: 0.5,
    });
  }

  public init() {
    const communicationCenter$ = observeCommunicationCenterFromRouteNavigationEnd(
      this._router,
      this._store,
      'all',
    );

    const unitsByCommCenter$ = communicationCenter$.pipe(
      undefinedOrSwitchMap((commCenter) =>
        this._store.select(selectUnitSummaryEntitiesByCommunicationCenterId(commCenter.id)),
      ),
      shareReplaySafe(1),
    );

    const isRequestPage$ = this._router.navigationEnd$.pipe(
      startWith(null),
      map((ev) => /\/cards\/.*?\/request/.test(ev?.url ?? '')),
    );

    const alertsByCommCenter$ = combineLatest([
      unitsByCommCenter$,
      this._positionReport.alerts$,
      isRequestPage$,
    ]).pipe(
      map(([units, alerts, isRequestPage]) =>
        units != null && alerts != null && units.count() > 0 && alerts.count() > 0 && !isRequestPage
          ? alerts.filter((alert) => units.find((u) => u.id === alert.unitId) != null)
          : undefined,
      ),
    );

    const hasAlerts$ = alertsByCommCenter$.pipe(map((alerts) => (alerts?.count() ?? 0) > 0));

    combineLatest([communicationCenter$, hasAlerts$, this._player.ready$])
      .pipe(
        map(
          ([commCenter, hasAlerts, audioReady]) =>
            commCenter?.playAlertForManualPositionReports === true && hasAlerts && audioReady,
        ),
        distinctUntilChanged(),
        takeUntil(this._onDestroy$),
      )
      .subscribe((canPlayAlerts) => {
        if (canPlayAlerts) {
          this._playLoop$.next(true);
        } else {
          this._playLoop$.next(false);
        }
      });

    const stopped$ = this._playLoop$.pipe(filter((play) => !play));
    this._playLoop$
      .pipe(
        distinctUntilChanged(),
        filter((play) => play),
        switchMap(() => this._tick.everyTenSeconds$.pipe(skip(1), takeUntil(stopped$))),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => this._player.play());
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
